import type { Locale, LocaleStrings } from "../ai_literacy/conceptCopy.js";

export {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  type Locale,
  type LocaleStrings,
  type StemTemplate,
} from "../ai_literacy/conceptCopy.js";

const EN: LocaleStrings = {
  titles: {
    scene_safety: "Check the scene before you help",
    bleeding_basic: "Pressure and when to get help for bleeding",
    emergency_call: "What to say when you call emergency services",
    recovery_position: "The recovery position and when to use it",
  },
  blurbs: {
    scene_safety:
      "A quick scan to see if the area is safe for you and the person—traffic, fire, or violence—before you step in or touch them.",
    bleeding_basic:
      "Using firm pressure, elevating a limb if appropriate, and knowing when the bleeding needs professional care right away.",
    emergency_call:
      "Calm, clear information: what happened, where you are, and the person’s condition, without guessing a diagnosis on the call.",
    recovery_position:
      "Rolling an unconscious but breathing person onto their side to help keep the airway open while help is coming.",
  },
  stems: [
    (b) => `Which first-aid topic is this about?\n\n“${b}”`,
    (b) => `Which topic best matches this description?\n\n${b}`,
    (b) => `For a first-aid course, this paragraph is about which topic?\n\n${b}`,
    (b) => `You’re taking notes. This sounds like which topic?\n\n«${b}»`,
    (b) => `From the first-aid map, this belongs to which topic?\n\n${b}`,
    (b) => `Match the description to the right topic name:\n\n${b}`,
  ],
};

const ES: LocaleStrings = {
  titles: {
    scene_safety: "Revisar el entorno antes de ayudar",
    bleeding_basic: "Presión y cuándo pedir ayuda con heridas",
    emergency_call: "Qué decir al llamar a emergencias",
    recovery_position: "Posición de seguridad y cuándo usarla",
  },
  blurbs: {
    scene_safety:
      "Una mirada rápida para comprobar si el lugar es seguro para ti y la persona: tráfico, fuego o riesgo de violencia.",
    bleeding_basic:
      "Usar presión firme, elevar un miembro si procede, y saber cuándo el sangrado requiere atención inmediata.",
    emergency_call:
      "Oración clara y en calma: qué ocurrió, dónde estás, el estado aproximado, sin adivinar un diagnóstico.",
    recovery_position:
      "Poner a una persona inconsciente pero con respiración de lado con la vía aérea cuidada mientras llega la ayuda.",
  },
  stems: [...EN.stems],
};

const EL: LocaleStrings = {
  titles: {
    scene_safety: "Έλεγχος σκηνής πριν βοηθήσεις",
    bleeding_basic: "Πίεση και πότε ζητάς βοήθεια για αιμορραγία",
    emergency_call: "Τι να πεις όταν καλείς τις πρώτες βοήθειες",
    recovery_position: "Θέση ανάνηψης και πότε τη χρησιμοποιείς",
  },
  blurbs: {
    scene_safety:
      "Γρήγορος έλεγχος αν ο χώρος είναι ασφαλής για εσένα και το άτομο — κυκλοφορία, φωτιά ή βία — πριν πλησιάσεις.",
    bleeding_basic:
      "Σταθερή πίεση, ανύψωση άκρου όταν είναι ασφαλές, και αναγνώριση πότε η αιμορραγία χρειάζεται άμεσα επαγγελματική φροντίδα.",
    emergency_call:
      "Ήρεμη και σαφής ενημέρωση: τι συνέβη, πού βρίσκεστε και ποια είναι η κατάσταση, χωρίς να μαντεύεις διάγνωση.",
    recovery_position:
      "Τοποθέτηση αναίσθητου αλλά αναπνέοντος ατόμου στο πλάι για να παραμένει ανοικτός ο αεραγωγός μέχρι να έρθει βοήθεια.",
  },
  stems: [...EN.stems],
};

export const COPY: Readonly<Record<Locale, LocaleStrings>> = { en: EN, es: ES, el: EL };

export function getCopy(locale: Locale = "en"): LocaleStrings {
  return COPY[locale] ?? EN;
}

export const CONCEPT_TITLES: Readonly<Record<string, string>> = EN.titles;
export const CONCEPT_BLURBS: Readonly<Record<string, string>> = EN.blurbs;

export function allConceptCopyIds(): string[] {
  return Object.keys(EN.titles);
}
